<template>
  <SBlankablePicture v-if="blankMedia" :media="blankMedia">
    <img ref="root" v-bind="mergeProps(imageBindings, $attrs)">
  </SBlankablePicture>
  <img v-else ref="root" v-bind="mergeProps(imageBindings, $attrs)">
</template>

<script lang="ts" setup>
import { mergeProps } from 'vue'
import { missingImageUrl, serializeAssetUrl } from '~/utils/format/imageUrl'
import { PLACEHOLDER_DEFAULT_WIDTH } from '~/utils/format/imageSizes'
import { placeholderWidthFor } from '~/composables/useImageFallback'

interface Props {
 placeholder?: string
 src?: string
 srcset?: string
 /**
  * A media query over which the image is not fetched at all: the browser
  * takes an inline blank candidate instead of `src`/`srcset`. Outside it, the
  * `<img>` loads as it does without this prop. See `PDP_TABLET_MEDIA`.
  *
  * The `<picture>` is rendered only with this prop; without it the `<img>` is
  * the root. A `v-show` on this component lands on `$attrs`, so on
  * the `<img>`, in the server HTML, but on the root element in the browser, and
  * hydration never removes the server's `display:none`: a wrapped `<img>` stayed
  * hidden for good (AB#64927). So this prop is not combinable with `v-show` on a
  * server-rendered image. Both `<img>` branches take `imageBindings`.
  */
 blankMedia?: string
 /**
  * Server-render the real `src`/`srcset` with native `loading="lazy"` instead
  * of the placeholder-until-intersection swap. The swap needs the app hydrated
  * and an IntersectionObserver callback before any request starts, which on a
  * slow phone left a category grid on placeholders for ~10 s after the HTML
  * arrived; the browser's own lazy loading starts in-view images from the HTML.
  */
 nativeLazy?: boolean
 /**
  * The real `src`/`srcset` in the server HTML, as with `nativeLazy`, but without
  * `loading="lazy"`: for an image in the first viewport. Implies `nativeLazy`.
  * A lazy image is not requested before the first layout, which waits for the
  * render-blocking CSS; an eager one is fetched as soon as the preload scanner
  * reaches it in the HTML.
  */
 eagerLoad?: boolean
 /**
  * The placeholder until the app is ready (onNuxtReady), then the real image
  * once it comes within DEFERRED_MARGIN of the viewport. Takes precedence over
  * `nativeLazy`. For images below the first viewport: native lazy loading starts
  * every image within ~1,250-2,500 px of it at the first layout, while the
  * scripts hydration waits for are still downloading. Chrome does not lazy-load
  * a src set after the placeholder has loaded, hence the observer.
  */
 deferLoad?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: undefined,
  src: undefined,
  srcset: undefined,
  blankMedia: undefined,
  nativeLazy: false,
  eagerLoad: false,
  deferLoad: false
})

// How far ahead of the viewport a deferLoad image is revealed: Chrome's native
// lazy-loading distance on a fast connection.
const DEFERRED_MARGIN = '1250px'

// The placeholder cannot be a prop default: a `withDefaults` value is part of
// the props definition and cannot reach runtime config. Resolving it here keeps
// it on the seam, so it is served from the media host instead of a pinned
// literal. `loading_image.png` is a root-level key with no folder segment
// (ADO 62973), and its 2000x2000 master is ~85KB against ~1.3KB at width
// 360 — so the transform matters more here than on the image it stands in
// for.
const resolvedPlaceholder = computed(() =>
  props.placeholder ?? serializeAssetUrl('loading_image.png', { width: 360, format: 'auto' }))

const emits = defineEmits<{
  (e: 'update:show-dialog', show: boolean): void
  (e: 'load' | 'intersect'): void
}>()
defineOptions({
  inheritAttrs: false
})
// Who lets the real image load, decided once per instance (callers pass a static
// prop). 'native': the real src is in the server HTML with loading="lazy" (none for
// eagerLoad) and the browser defers it. 'observer': the placeholder stays until an
// IntersectionObserver reports the image in view. 'deferred': 'observer', started
// when the app is ready and with a margin, as native lazy loading has. Each mode's
// mount step is in `mountByMode` below.
type RevealMode = 'native' | 'observer' | 'deferred'
const revealMode = computed<RevealMode>(() => {
  if (props.eagerLoad) {
    return 'native'
  }
  if (props.deferLoad) {
    return 'deferred'
  }
  return props.nativeLazy ? 'native' : 'observer'
})

// Whether the real image may be requested.
const revealed = ref(revealMode.value === 'native')
const observer = ref<IntersectionObserver>()
const loaded = ref(false)
const intersectionOptions = ref({})
const root = useTemplateRef('root')

/**
 * Client-side replacement for Cloudinary's `d_` default-image parameter, which
 * substituted a missing image at delivery time and returned 200. Cloudflare has
 * no equivalent, and `/cdn-cgi/` fires neither Rules Snippets nor Workers routes
 * (measured 2026-09-08), so nothing server-side can intercept the 404 — the swap
 * has to happen here (ADO 62991).
 *
 * Held as reactive state rather than written onto the element: `src` is bound,
 * so a DOM write would be overwritten by the next render.
 */
const failed = ref(false)

// Reset when a different image is bound. Vue reuses this component instance
// across an index-keyed `v-for`, and a sticky `failed` would keep serving the
// missing-image placeholder for every later src on that instance — including
// ones that load perfectly.
// Captured at error time: a computed must not read layout, and the placeholder
// has to match the slot it fills or a hi-res hero gets a blurry 360px upscale.
const failedWidth = ref<number>()

watch(() => props.src, () => {
  failed.value = false
  failedWidth.value = undefined
})

const fail = (image: HTMLImageElement | null): void => {
  if (failed.value) {
    // Applied once. Without this the placeholder failing in turn would set the
    // same src again, re-fire `error`, and loop.
    return
  }

  // A failed *loading* placeholder is a different fault, and masking it with the
  // missing-image placeholder would hide it. Only a failed real image swaps.
  //
  // The second half of that test is load-bearing: a caller may deliberately
  // pass `placeholder` equal to `src` to skip the loading state — as
  // ProductThumbnailImage does — and for those the failing placeholder *is* the
  // failing image. Comparing the URL alone made this guard true on every error
  // for that caller, so no product thumbnail ever reached the placeholder.
  const currentSrc = image?.getAttribute('src')
  if (currentSrc === resolvedPlaceholder.value && currentSrc !== props.src) {
    return
  }

  failedWidth.value = image ? placeholderWidthFor(image) : undefined
  failed.value = true
}

const onError = (event: Event): void => {
  fail(event.target as HTMLImageElement | null)
}

const srcImage = computed(() => {
  if (failed.value) {
    return missingImageUrl({ width: failedWidth.value ?? PLACEHOLDER_DEFAULT_WIDTH, format: 'auto' })
  }
  return revealed.value && props.src ? props.src : resolvedPlaceholder.value
})
// A placeholder equal to src means the caller opted out of the loading-image
// swap, as ProductThumbnailImage does. Withholding srcset until intersection
// then made the browser fetch src and abort it once srcset arrived and it
// re-selected a candidate - one cancelled request per PDP carousel slide
// (ADO 63506). Compared against the resolved placeholder, like `load` below.
const eager = computed(() => !!props.src && resolvedPlaceholder.value === props.src)
// Dropped on failure: the handler cannot see which candidate the browser chose,
// and leaving the list in place lets it reselect an original URL and overwrite
// the placeholder.
const srcsetImage = computed(() => {
  if (failed.value) { return undefined }
  return (revealed.value || eager.value) && props.srcset ? props.srcset : undefined
})
const intersectionAvailable = computed(() => typeof window !== 'undefined' && 'IntersectionObserver' in window)
// Gated against the *resolved* placeholder, not the prop: `srcImage` renders
// `resolvedPlaceholder` until intersection, and most callers pass no
// `placeholder` at all — so comparing the prop matched nothing and let the
// placeholder's own load event report the real image as loaded.
//
// When the placeholder is the src, every load is the real image, and the
// comparison below could never pass - so `loaded` and `load` never fired.
const load = () => {
  if (eager.value || root.value?.getAttribute('src') !== resolvedPlaceholder.value) {
    loaded.value = true
    emits('load')
  }
}

// What both `<img>` branches of the template carry, ahead of the caller's
// `$attrs` (which `mergeProps` lets override these, and merges into the class
// and handlers). Key order is attribute order on a client render, so
// loading/decoding come first: a src set before loading="lazy" starts an eager
// fetch.
const imageBindings = computed(() => ({
  loading: revealMode.value === 'native' && !props.eagerLoad ? 'lazy' as const : undefined,
  decoding: revealMode.value === 'native' ? 'async' as const : undefined,
  src: srcImage.value,
  srcset: srcsetImage.value,
  'src-placeholder': resolvedPlaceholder.value,
  class: ['lazy-image', { 'v-lazy-image-loaded': loaded.value }],
  onLoad: load,
  onError,
}))

// The server HTML carries the real src, so the image can settle before hydration
// attaches @load/@error, and those events do not fire again. A settled image with
// no pixels failed (raster product images only: an SVG without intrinsic size
// would read the same).
const adoptSettledImage = () => {
  const image = root.value
  if (!image?.complete) {
    return
  }
  if (image.naturalWidth === 0) {
    fail(image)
  } else {
    load()
  }
}

const revealWhenInView = () => {
  if (!intersectionAvailable.value) {
    revealed.value = true
    return
  }
  observer.value = new IntersectionObserver((entries) => {
    const [image] = entries.slice(-1)
    if (image?.isIntersecting) {
      revealed.value = true
      cleanup()
      emits('intersect')
    }
  }, intersectionOptions.value)
  observer.value.observe(root.value!)
}

const mountByMode: Record<RevealMode, () => void> = {
  native: adoptSettledImage,
  observer: revealWhenInView,
  // onNuxtReady runs its callback on an idle callback, so the grid can have
  // re-rendered (sort, filter, navigation) and unmounted the image by then.
  deferred: () => onNuxtReady(() => {
    if (!root.value) {
      return
    }
    intersectionOptions.value = { rootMargin: `${DEFERRED_MARGIN} 0px` }
    revealWhenInView()
  })
}

onMounted(() => mountByMode[revealMode.value]())

const cleanup = () => {
  if (observer.value) {
    observer.value.disconnect()
  }
}

onUnmounted(cleanup)

</script>

<style lang="scss" scoped>
.lazy-image {
  display: block;
  width: 100%;
  max-width: 100%;
}
</style>
